// Kiểu dữ liệu + dữ liệu mẫu cho module POS (giai đoạn UI-first, chưa có backend)

export type ItemType = 'service' | 'product' | 'package';
export type DiscountType = '%' | 'đ';
export type PosRole = 'cashier' | 'manager' | 'admin';
export type PaymentMethod = 'cash' | 'transfer' | 'card' | 'ewallet' | 'prepaid';
export type OrderStatus = 'completed' | 'debt' | 'partially_refunded' | 'refunded' | 'cancelled';
export type RefundStatus = 'pending' | 'approved' | 'paid' | 'rejected';
export type TxnKind = 'payment' | 'debt_collection' | 'refund';
export type OrderSource = 'pos' | 'myspa';

export interface Branch { id: string; name: string; short: string; prefix: string }

export interface CatalogItem {
  id: string;
  code: string;
  name: string;
  price: number;
  type: ItemType;
  group: string;
  /** Tồn theo chi nhánh; null với dịch vụ / gói */
  stock: Record<string, number> | null;
  /** Số buổi của gói liệu trình */
  sessions?: number;
  badge?: string;
  /** Mã vạch (code_2 của sản phẩm trên hệ thống) – quét / tìm như mã hàng */
  barcode?: string;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  tier: 'Thường' | 'Bạc' | 'Vàng' | 'Kim cương';
  prepaid: number;
  source?: string;
}

export interface Staff { id: string; name: string; branchId: string }
export interface Manager { id: string; name: string; branchIds: string[]; pin: string }

export interface Promotion {
  id: string;
  name: string;
  /** item: giảm theo dòng hàng · order: giảm theo đơn */
  type: 'item' | 'order';
  valueType: DiscountType;
  value: number;
  maxDiscount?: number;
  minOrder?: number;
  itemIds: string[];
  branchIds: string[] | 'all';
  startAt: string;
  endAt: string;
  stackable: boolean;
  priority: number;
  enabled: boolean;
  usedCount: number;
}

export interface Voucher {
  code: string;
  name: string;
  valueType: DiscountType;
  value: number;
  maxDiscount?: number;
  minOrder: number;
  expiresAt: string;
  usageLimit: number;
  used: number;
  enabled: boolean;
}

export interface PosConfig {
  allowDebt: boolean;
  discountLimit: Record<PosRole, number>;
  refundApprovalThreshold: number;
  blockOutOfStock: boolean;
  roundingUnit: number;
  draftTtlDays: number;
  maxPaymentLines: number;
  bank: { bankId: string; accountNo: string; accountName: string };
}

/** Dòng trong giỏ hàng (chưa tính giá) */
export interface CartLine {
  key: string;
  itemId: string;
  code: string;
  name: string;
  type: ItemType;
  listPrice: number;
  qty: number;
  discountType: DiscountType;
  discountValue: number;
  staffId: string;
  /** Tên KTV khi nhân viên lấy từ API (không thuộc danh sách STAFF mẫu) */
  staffName?: string;
  note: string;
  /** Dòng lấy từ dịch vụ của lịch hẹn (id dịch vụ phía CMS) — dùng để đối chiếu chênh lệch */
  aptServiceId?: string;
}

/** Dòng đã tính giá — lưu vào đơn để hoàn tiền về sau */
export interface PricedLine extends CartLine {
  gross: number;
  promoAmount: number;
  promoName?: string;
  lineDiscountAmount: number;
  afterLine: number;
  allocated: number;
  net: number;
  refundedQty: number;
  refundedAmount: number;
}

export interface OrderTotals {
  subtotal: number;
  itemPromo: number;
  lineDiscount: number;
  orderPromo: number;
  orderPromoName?: string;
  orderDiscount: number;
  voucher: number;
  rounding: number;
  total: number;
}

export interface PaymentLineInput {
  method: PaymentMethod;
  amount: number;
  tendered: number;
  txnRef: string;
}

export interface Transaction {
  id: string;
  orderId: string;
  branchId: string;
  at: string;
  kind: TxnKind;
  method: PaymentMethod;
  /** Âm với giao dịch hoàn */
  amount: number;
  txnRef: string;
  tendered?: number;
  change?: number;
  cashier: string;
  refundId?: string;
}

export interface OrderLog { at: string; by: string; action: string }

export interface Order {
  id: string;
  branchId: string;
  createdAt: string;
  cashier: string;
  customerId: string | null;
  customerName: string;
  customerPhone: string;
  lines: PricedLine[];
  totals: OrderTotals;
  orderDiscountInput: { type: DiscountType; value: number };
  voucherCode: string;
  approvedBy?: string;
  note: string;
  paid: number;
  refunded: number;
  debt: number;
  status: OrderStatus;
  source: OrderSource;
  importBatchId?: string;
  printCount: number;
  cancelReason?: string;
  logs: OrderLog[];
  /** Đơn lên từ lịch hẹn đã tư vấn: giữ bản chụp dịch vụ lúc tư vấn để đối chiếu */
  appointment?: AppointmentSnapshot;
}

/** Dịch vụ trên lịch hẹn (giá lúc tư vấn, giảm giá đã ghi trên lịch hẹn) */
export interface AppointmentLine {
  serviceId: string;
  name: string;
  price: number;
  qty: number;
  /** Tổng giảm của cả dòng */
  discount: number;
  code?: string;
  staffId?: string;
  staffName?: string;
}

/** Khách hàng của lịch hẹn theo API POS V2 */
export interface AppointmentCustomer {
  id: number;
  name: string;
  phone: string;
  rank: string;
  /** Công nợ hiện tại của khách */
  debt: number;
  /** Số dư ví trả trước; null khi API chưa trả trường này */
  wallet: number | null;
}

export interface AppointmentSnapshot {
  id: string;
  code: string;
  /** Đường dẫn chi tiết lịch hẹn bên CMS */
  path: string;
  consultant: string;
  lines: AppointmentLine[];
  /** Tổng theo lịch hẹn = Σ(giá × SL − giảm trên lịch hẹn) */
  total: number;
  /** Có khi giỏ được nạp từ API POS V2 — thanh toán sẽ gọi API thay vì lưu đơn ở máy */
  remote?: boolean;
  customer?: AppointmentCustomer | null;
  branch?: { id: number; name: string } | null;
}

/** Lịch hẹn S7 – Xác nhận làm, đang chờ thu ngân (API POS V2 – waiting-orders) */
export interface WaitingAppointment {
  id: string;
  code: string;
  customerName: string;
  customerPhone: string;
  branchId: number | null;
  branchName: string;
  tagName: string;
  consultant: string;
  /** ISO; rỗng nếu API không có */
  scheduledAt: string;
  /** ISO – lúc chuyển sang "Xác nhận làm" */
  waitingSince: string;
  primaryService: string;
  itemsCount: number;
  total: number;
  /** Loại hàng có trong lịch hẹn – API chưa trả (đề xuất `item_types`); undefined = chưa có dữ liệu */
  itemTypes?: ItemType[];
}

export interface RefundLine { lineKey: string; name: string; qty: number; amount: number; stockAction?: 'restock' | 'discard' }

export interface Refund {
  id: string;
  orderId: string;
  branchId: string;
  createdAt: string;
  createdBy: string;
  lines: RefundLine[];
  amount: number;
  method: PaymentMethod;
  txnRef: string;
  reason: string;
  status: RefundStatus;
  approvedBy?: string;
  approvedAt?: string;
  paidAt?: string;
  rejectReason?: string;
}

export interface Draft {
  id: string;
  name: string;
  branchId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  customerId: string | null;
  /** Khách lấy từ hệ thống (không có trong danh sách khách POS) – lưu kèm để mở lại nháp */
  customer?: Customer | null;
  lines: CartLine[];
  orderDiscount: { type: DiscountType; value: number };
  voucherCode: string;
  note: string;
  total: number;
  lockedBy: string | null;
  lockedAt: string | null;
  /** Nháp đang lên dở từ lịch hẹn */
  appointment?: AppointmentSnapshot | null;
}

export interface ShiftClose {
  id: string;
  branchId: string;
  at: string;
  by: string;
  expected: Record<PaymentMethod, number>;
  counted: Record<PaymentMethod, number>;
  note: string;
}

export interface ImportBatch {
  id: string;
  fileName: string;
  at: string;
  by: string;
  totalRows: number;
  orders: number;
  success: number;
  skipped: number;
  failed: number;
  mode: 'skip' | 'overwrite';
  status: 'done' | 'rolled_back';
}

// ---------------------------------------------------------------------------

export const BRANCHES: Branch[] = [
  { id: 'q1', name: 'Khothi Quận 1', short: 'Q1', prefix: 'Q1' },
  { id: 'q3', name: 'Khothi Quận 3', short: 'Q3', prefix: 'Q3' },
  { id: 'q10', name: 'Khothi Quận 10', short: 'Q10', prefix: 'Q10' },
];

export const branchName = (id: string) => BRANCHES.find(b => b.id === id)?.name || id;

export const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Tiền mặt',
  transfer: 'Chuyển khoản',
  card: 'Quẹt thẻ',
  ewallet: 'Ví điện tử',
  prepaid: 'Ví trả trước',
};
export const METHODS: PaymentMethod[] = ['cash', 'transfer', 'card', 'ewallet', 'prepaid'];
/** Phương thức bắt buộc mã giao dịch (BR2.2) */
export const METHODS_NEED_REF: PaymentMethod[] = ['transfer', 'card', 'ewallet'];

export const TYPE_LABEL: Record<ItemType, string> = { service: 'Dịch vụ', product: 'Sản phẩm', package: 'Gói' };

export const DEFAULT_CONFIG: PosConfig = {
  allowDebt: true,
  discountLimit: { cashier: 10, manager: 30, admin: 100 },
  refundApprovalThreshold: 2_000_000,
  blockOutOfStock: false,
  roundingUnit: 1000,
  draftTtlDays: 7,
  maxPaymentLines: 5,
  // Tài khoản mẫu — thay bằng tài khoản thật của công ty khi có backend
  bank: { bankId: 'VCB', accountNo: '0071000000000', accountName: 'CONG TY KHOTHI' },
};

export const CATALOG: CatalogItem[] = [
  { id: 's1', code: 'DV-THERMAGE', name: 'Thermage FLX', price: 45_000_000, type: 'service', group: 'Trẻ hóa da', stock: null },
  { id: 's2', code: 'DV-HIFU', name: 'Nâng cơ Hifu', price: 15_000_000, type: 'service', group: 'Nâng cơ', stock: null },
  { id: 's3', code: 'DV-TAMTRANG', name: 'Tắm trắng', price: 5_000_000, type: 'service', group: 'Chăm sóc da', stock: null, badge: 'Hot' },
  { id: 's4', code: 'DV-LASER', name: 'Laser Pico', price: 3_500_000, type: 'service', group: 'Laser', stock: null },
  { id: 's5', code: 'DV-CSD', name: 'Chăm sóc da cơ bản', price: 650_000, type: 'service', group: 'Chăm sóc da', stock: null },
  { id: 's6', code: 'DV-TRIMUN', name: 'Trị mụn chuyên sâu', price: 1_200_000, type: 'service', group: 'Chăm sóc da', stock: null },
  { id: 'p1', code: 'SP-SERUMB5', name: 'Serum B5', price: 450_000, type: 'product', group: 'Mỹ phẩm', stock: { q1: 1, q3: 10, q10: 6 } },
  { id: 'p2', code: 'SP-KCN', name: 'Kem chống nắng', price: 850_000, type: 'product', group: 'Mỹ phẩm', stock: { q1: 0, q3: 4, q10: 2 } },
  { id: 'p3', code: 'SP-SRM', name: 'Sữa rửa mặt dịu nhẹ', price: 320_000, type: 'product', group: 'Mỹ phẩm', stock: { q1: 25, q3: 12, q10: 8 } },
  { id: 'p4', code: 'SP-MASK', name: 'Mặt nạ phục hồi (hộp 5)', price: 550_000, type: 'product', group: 'Mỹ phẩm', stock: { q1: 9, q3: 0, q10: 3 } },
  { id: 'k1', code: 'GOI-HIFU5', name: 'Gói Hifu 5 buổi', price: 60_000_000, type: 'package', group: 'Nâng cơ', stock: null, sessions: 5 },
  { id: 'k2', code: 'GOI-TRIMUN10', name: 'Gói trị mụn 10 buổi', price: 10_000_000, type: 'package', group: 'Chăm sóc da', stock: null, sessions: 10 },
];

export const CATALOG_GROUPS = Array.from(new Set(CATALOG.map(c => c.group)));

export const SEED_CUSTOMERS: Customer[] = [
  { id: 'c1', name: 'Nguyễn Bích Ngọc', phone: '0901234567', tier: 'Vàng', prepaid: 15_000_000 },
  { id: 'c2', name: 'Trần Thị Mai', phone: '0912345678', tier: 'Kim cương', prepaid: 0 },
  { id: 'c3', name: 'Lê Hoàng Anh', phone: '0987654321', tier: 'Thường', prepaid: 0 },
  { id: 'c4', name: 'Phạm Thu Hà', phone: '0934567890', tier: 'Bạc', prepaid: 2_000_000 },
  { id: 'c5', name: 'Võ Minh Thư', phone: '0978123456', tier: 'Vàng', prepaid: 500_000 },
  { id: 'c6', name: 'Đặng Quỳnh Như', phone: '0389123456', tier: 'Thường', prepaid: 0 },
  { id: 'c7', name: 'Bùi Ngọc Lan', phone: '0868123456', tier: 'Bạc', prepaid: 0 },
  { id: 'c8', name: 'Hoàng Gia Hân', phone: '0707123456', tier: 'Kim cương', prepaid: 8_000_000 },
];

export const STAFF: Staff[] = [
  { id: 'lan', name: 'Lan', branchId: 'q1' },
  { id: 'mai', name: 'Mai', branchId: 'q1' },
  { id: 'ha', name: 'Hà', branchId: 'q1' },
  { id: 'minh', name: 'Minh', branchId: 'q3' },
  { id: 'thao', name: 'Thảo', branchId: 'q3' },
  { id: 'nhi', name: 'Nhi', branchId: 'q10' },
  { id: 'tuan', name: 'Tuấn', branchId: 'q10' },
];
export const staffName = (id: string) => STAFF.find(s => s.id === id)?.name || '';

// PIN chỉ là dữ liệu mẫu để thử luồng duyệt — backend thật sẽ kiểm tra PIN phía server
export const MANAGERS: Manager[] = [
  { id: 'm1', name: 'Quản lý Hà (Q1)', branchIds: ['q1'], pin: '123456' },
  { id: 'm2', name: 'Quản lý Minh (Q3)', branchIds: ['q3'], pin: '654321' },
  { id: 'm3', name: 'Quản lý Tuấn (Q10)', branchIds: ['q10'], pin: '111111' },
];

const iso = (d: Date) => d.toISOString();
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

export const seedPromotions = (now: Date): Promotion[] => [
  {
    id: 'km1', name: 'Thermage tháng này -10%', type: 'item', valueType: '%', value: 10, itemIds: ['s1'], branchIds: 'all',
    startAt: iso(addDays(now, -20)), endAt: iso(addDays(now, 20)), stackable: false, priority: 50, enabled: true, usedCount: 18,
  },
  {
    id: 'km2', name: 'Tắm trắng giảm 500.000đ', type: 'item', valueType: 'đ', value: 500_000, itemIds: ['s3'], branchIds: ['q1', 'q3'],
    startAt: iso(addDays(now, -5)), endAt: iso(addDays(now, 10)), stackable: false, priority: 40, enabled: true, usedCount: 7,
  },
  {
    id: 'km3', name: 'Đơn từ 80 triệu giảm 3%', type: 'order', valueType: '%', value: 3, maxDiscount: 5_000_000, minOrder: 80_000_000, itemIds: [], branchIds: 'all',
    startAt: iso(addDays(now, -30)), endAt: iso(addDays(now, 60)), stackable: false, priority: 30, enabled: true, usedCount: 2,
  },
  {
    id: 'km4', name: 'Black Friday mỹ phẩm -20%', type: 'item', valueType: '%', value: 20, itemIds: ['p1', 'p2', 'p3', 'p4'], branchIds: 'all',
    startAt: iso(addDays(now, 40)), endAt: iso(addDays(now, 45)), stackable: false, priority: 60, enabled: true, usedCount: 0,
  },
];

export const seedVouchers = (now: Date): Voucher[] => [
  { code: 'KHOTHI10', name: 'Giảm 10% tối đa 1 triệu', valueType: '%', value: 10, maxDiscount: 1_000_000, minOrder: 1_000_000, expiresAt: iso(addDays(now, 30)), usageLimit: 200, used: 41, enabled: true },
  { code: 'CHAOBAN200', name: 'Khách mới giảm 200.000đ', valueType: 'đ', value: 200_000, minOrder: 500_000, expiresAt: iso(addDays(now, 60)), usageLimit: 500, used: 120, enabled: true },
  { code: 'HE2026', name: 'Ưu đãi hè (đã hết hạn)', valueType: '%', value: 15, maxDiscount: 2_000_000, minOrder: 0, expiresAt: iso(addDays(now, -10)), usageLimit: 100, used: 63, enabled: true },
  { code: 'VIP500', name: 'VIP giảm 500.000đ (hết lượt)', valueType: 'đ', value: 500_000, minOrder: 5_000_000, expiresAt: iso(addDays(now, 30)), usageLimit: 20, used: 20, enabled: true },
];
